<script lang="ts">
	// Formatters/i18n
	import { format } from 'src/services/i18n';

	// Svelte
	import { createEventDispatcher } from 'svelte';
	import { replace } from 'svelte-spa-router';

	// Design system components
	import { Button, Text, toast } from '@Shelly/shelly-design-system/index';

	// Components
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import UnitTransferCreateAccountModal from 'src/components/Settings/HomeManagement/UnitTransferCreateAccountModal.svelte';

	// Helpers
	import { current_time_zone } from 'src/helpers/date_helper';
	import {
		DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS,
		getSignUpRegisterAlert,
		showSignUpSuccessToast,
		validateSignUpPassword,
	} from 'src/helpers/signup_helpers';
	import type { InputProperties } from 'src/types/InputProperties';

	// Services
	import { declineUnitTransferAuth } from 'src/services/iotu/house-management';
	import { handleRegister } from 'src/services/http';

	export let token = '';
	export let email = '';
	export let unitId = 0;

	const dispatch = createEventDispatcher<{ error: string }>();

	let isLoading = false;
	let pendingAction: 'decline' | 'leave' | null = null;
	let showCreateAccountModal = false;
	let passwordError = '';
	let passwordInputProps: InputProperties = { ...DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS };

	async function declineTransfer(): Promise<void> {
		const response = await declineUnitTransferAuth(token, email, unitId);
		const tokenInvalid = Boolean(response?.errors?.invalid_token);

		if (!response?.isok && !tokenInvalid) {
			throw new Error('Decline failed');
		}
	}

	async function confirmDecline(): Promise<void> {
		if (isLoading) return;

		isLoading = true;
		pendingAction = 'decline';

		try {
			await declineTransfer();
			showCreateAccountModal = true;
		} catch {
			dispatch(
				'error',
				$format('error_decline_unit_transfer', {
					default: 'Failed to decline the transfer',
				}),
			);
		} finally {
			isLoading = false;
			pendingAction = null;
		}
	}

	async function declineAndLeave(): Promise<void> {
		if (isLoading) return;

		isLoading = true;
		pendingAction = 'leave';

		try {
			await declineTransfer();
			navigateToLogin();
		} catch {
			dispatch(
				'error',
				$format('error_decline_unit_transfer', {
					default: 'Failed to decline the transfer',
				}),
			);
		} finally {
			isLoading = false;
			pendingAction = null;
		}
	}

	async function createAccountWithoutTransfer(): Promise<void> {
		if (isLoading) return;

		const nextPasswordError = validateSignUpPassword(passwordInputProps.value, $format);
		if (nextPasswordError) {
			passwordError = nextPasswordError;
			return;
		}

		isLoading = true;

		try {
			const res = await handleRegister(email, passwordInputProps.value, current_time_zone());

			if (!res?.isok) {
				const alert = getSignUpRegisterAlert(res, $format);
				toast.open({
					type: 'danger',
					duration: 3000,
					title: alert.message || alert.heading,
				});
				return;
			}

			showCreateAccountModal = false;
			showSignUpSuccessToast($format);
			navigateToLogin();
		} catch {
			toast.open({
				type: 'danger',
				duration: 3000,
				title: $format('error_unit_transfer_registration', {
					default: 'Failed to create the account and complete the transfer',
				}),
			});
		} finally {
			isLoading = false;
		}
	}

	function navigateToLogin(): void {
		replace('/login');
	}

	function onCreateAccountModalClose(): void {
		showCreateAccountModal = false;
		navigateToLogin();
	}
</script>

<TransitionWrapper>
	<Text element="h1" class="mb-l">
		{$format('unit_transfer_decline_confirm_title', {
			default: 'Decline home transfer?',
		})}
	</Text>
	<Text class="mb-l">
		{$format('unit_transfer_decline_confirm_message', {
			default:
				'If you decline, you will not receive this home. You can still create a Shelly account later from the sign-up page.',
		})}
	</Text>
	<Button
		class="text-capitalize mb-s"
		variant="primary"
		width="full"
		loading={pendingAction === 'decline'}
		disabled={isLoading}
		aria-label={$format('button_decline_transfer', { default: 'Decline' })}
		on:click={confirmDecline}
	>
		{$format('button_decline_transfer', { default: 'Decline' })}
	</Button>
	<Button
		variant="secondary"
		width="full"
		loading={pendingAction === 'leave'}
		disabled={isLoading}
		aria-label={$format('link_back_to_shelly', { default: 'Back to Shelly' })}
		on:click={declineAndLeave}
	>
		{$format('link_back_to_shelly', { default: 'Back to Shelly' })}
	</Button>
</TransitionWrapper>

<UnitTransferCreateAccountModal
	bind:showModal={showCreateAccountModal}
	bind:passwordInputProps
	bind:passwordError
	loading={isLoading}
	collectPassword={true}
	alertTitleKey="unit_transfer_declined_create_account_title"
	alertTitleDefault="Home transfer was declined"
	alertMessageKey="unit_transfer_declined_create_account_message"
	alertMessageDefault="You declined this home transfer. You can still create your Shelly account now."
	on:confirm={createAccountWithoutTransfer}
	on:close={onCreateAccountModalClose}
/>
